import {
  INVENTORY_SNAPSHOT_MAPPING_SCHEMA,
  InventorySnapshotAthena,
  MappingSchemaField,
} from '@warebee/shared/import-converter';
import { TFunction } from 'i18next';
import { keyBy } from 'lodash';
import i18n from '../../../i18n';
import { MappingSettings } from '../../store/import.types';

export function getInventorySnapshotSchema(
  t: TFunction<'dataset'> = i18n.t,
): MappingSettings<InventorySnapshotAthena> {
  const baseFields = keyBy(
    INVENTORY_SNAPSHOT_MAPPING_SCHEMA.fields,
    f => f.name,
  ) as Record<
    keyof InventorySnapshotAthena,
    MappingSchemaField<InventorySnapshotAthena>
  >;

  return {
    ...INVENTORY_SNAPSHOT_MAPPING_SCHEMA,
    fields: [
      {
        ...baseFields.eventId,
        title: t('eventId', { ns: 'dataset' }),
        aliases: ['eventId'],
      },
      {
        ...baseFields.objectId,
        title: t('objectId', { ns: 'dataset' }),
        aliases: ['objectId'],
      },
      {
        ...baseFields.logisticsUnitId,
        title: t('logisticsUnitId', { ns: 'dataset' }),
        aliases: ['logisticsUnitId'],
      },
      {
        ...baseFields.snapshotTimestamp,
        title: t('snapshotTimestamp', { ns: 'dataset' }),
        aliases: ['snapshotTimestamp'],
      },
      {
        ...baseFields.assignmentTimestampStart,
        title: t('assignmentTimestampStart', { ns: 'dataset' }),
        aliases: ['assignmentTimestampStart'],
      },
      {
        ...baseFields.assignmentTimestampEnd,
        title: t('assignmentTimestampEnd', { ns: 'dataset' }),
        aliases: ['assignmentTimestampEnd'],
      },
      {
        ...baseFields.receivedDate,
        title: t('receivedDate', { ns: 'dataset' }),
        aliases: ['receivedDate'],
      },
      {
        ...baseFields.agingDate,
        title: t('agingDate', { ns: 'dataset' }),
        aliases: ['agingDate'],
      },
      {
        ...baseFields.expirationDate,
        title: t('expirationDate', { ns: 'dataset' }),
        aliases: ['expirationDate'],
      },
      {
        ...baseFields.lastMovementDate,
        title: t('lastMovementDate', { ns: 'dataset' }),
        aliases: ['lastMovementDate'],
      },
      {
        ...baseFields.consignee,
        title: t('consignee', { ns: 'dataset' }),
        aliases: ['consignee'],
      },
      {
        ...baseFields.sku,
        title: t('sku', { ns: 'dataset' }),
        aliases: ['sku'],
      },
      {
        ...baseFields.locationId,
        title: t('locationId', { ns: 'dataset' }),
        aliases: ['locationId'],
      },
      {
        ...baseFields.lot,
        title: t('lot', { ns: 'dataset' }),
        aliases: ['lot'],
      },
      {
        ...baseFields.serialNumber,
        title: t('serialNumber', { ns: 'dataset' }),
        aliases: ['serialNumber'],
      },
      {
        ...baseFields.quantity,
        title: t('quantity', { ns: 'dataset' }),
        aliases: ['quantity'],
      },
      {
        ...baseFields.uom,
        title: t('uom', { ns: 'dataset' }),
        aliases: ['uom'],
      },
      {
        ...baseFields.locationUom,
        title: t('locationUom', { ns: 'dataset' }),
        aliases: ['locationUom'],
      },
      {
        ...baseFields.inventoryStatus,
        title: t('inventoryStatus', { ns: 'dataset' }),
        aliases: ['inventoryStatus'],
      },
      {
        ...baseFields.stockType,
        title: t('stockType', { ns: 'dataset' }),
        aliases: ['stockType'],
      },
      {
        ...baseFields.minQty,
        title: t('minQty', { ns: 'dataset' }),
        aliases: ['minQty'],
      },
      {
        ...baseFields.maxQty,
        title: t('maxQty', { ns: 'dataset' }),
        aliases: ['maxQty'],
      },
      {
        ...baseFields.permanent,
        title: t('permanent', { ns: 'dataset' }),
        aliases: ['permanent'],
      },
      {
        ...baseFields.unitCost,
        title: t('unitCost', { ns: 'dataset' }),
        aliases: ['unitCost'],
      },
      {
        ...baseFields.totalValue,
        title: t('totalValue', { ns: 'dataset' }),
        aliases: ['totalValue'],
      },
      {
        ...baseFields.temperatureZone,
        title: t('temperatureZone', { ns: 'dataset' }),
        aliases: ['temperatureZone'],
      },
      {
        ...baseFields.temperatureReading,
        title: t('temperatureReading', { ns: 'dataset' }),
        aliases: ['temperatureReading'],
      },
      {
        ...baseFields.allocatedQty,
        title: t('allocatedQty', { ns: 'dataset' }),
        aliases: ['allocatedQty'],
      },
      {
        ...baseFields.availableQty,
        title: t('availableQty', { ns: 'dataset' }),
        aliases: ['availableQty'],
      },
      {
        ...baseFields.onHoldQty,
        title: t('onHoldQty', { ns: 'dataset' }),
        aliases: ['onHoldQty'],
      },
      {
        ...baseFields.cycleCountDate,
        title: t('cycleCountDate', { ns: 'dataset' }),
        aliases: ['cycleCountDate'],
      },
      {
        ...baseFields.cycleCountVariance,
        title: t('cycleCountVariance', { ns: 'dataset' }),
        aliases: ['cycleCountVariance'],
      },
      {
        ...baseFields.abcClass,
        title: t('abcClass', { ns: 'dataset' }),
        aliases: ['abcClass'],
      },
      {
        ...baseFields.lastMovementType,
        title: t('lastMovementType', { ns: 'dataset' }),
        aliases: ['lastMovementType'],
      },
      {
        ...baseFields.lastMovementReference,
        title: t('lastMovementReference', { ns: 'dataset' }),
        aliases: ['lastMovementReference'],
      },
      {
        ...baseFields.qualityGrade,
        title: t('qualityGrade', { ns: 'dataset' }),
        aliases: ['qualityGrade'],
      },
      {
        ...baseFields.quarantineReason,
        title: t('quarantineReason', { ns: 'dataset' }),
        aliases: ['quarantineReason'],
      },
      {
        ...baseFields.supplier,
        title: t('supplier', { ns: 'dataset' }),
        aliases: ['supplier'],
      },
      {
        ...baseFields.poNumber,
        title: t('poNumber', { ns: 'dataset' }),
        aliases: ['poNumber'],
      },
      {
        ...baseFields.warehouse,
        title: t('warehouse', { ns: 'dataset' }),
        aliases: ['warehouse'],
      },
      {
        ...baseFields.weight,
        title: t('weight', { ns: 'dataset' }),
        aliases: ['weight'],
      },
      {
        ...baseFields.volume,
        title: t('volume', { ns: 'dataset' }),
        aliases: ['volume'],
      },
      {
        ...baseFields.sourceSystem,
        title: t('sourceSystem', { ns: 'dataset' }),
        aliases: ['sourceSystem'],
      },
      {
        ...baseFields.externalId,
        title: t('externalId', { ns: 'dataset' }),
        aliases: ['externalId'],
      },
      {
        ...baseFields.complianceStatus,
        title: t('complianceStatus', { ns: 'dataset' }),
        aliases: ['complianceStatus'],
      },
      {
        ...baseFields.inboundReference,
        title: t('inboundReference', { ns: 'dataset' }),
        aliases: ['inboundReference'],
      },
      {
        ...baseFields.outboundReservation,
        title: t('outboundReservation', { ns: 'dataset' }),
        aliases: ['outboundReservation'],
      },
    ],
  };
}
